import React from "react";
import {
  AreaChart,
  Tooltip,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import clsx from "clsx";
import useInterval from "@rooks/use-interval";
import { useEffect, useState } from "react";

import axios from "../../../api/axios";
import useConfig from "../../../hooks/useConfig";
import useAuth from "../../../hooks/authenticate/useAuth";

var request_updateTimelineData_safe = true;

function getLocalHour(date) {
  if (date) {
    let hour = new Date(date).getHours();
    return hour < 10 ? `0${hour}:00` : `${hour}:00`;
  }
}
function findHourData(timeline, hour) {
  for (let i = 0; i < timeline.length; i++) {
    if (timeline[i]["hour"] === hour) {
      return timeline[i];
    }
  }
  return -1;
}

function margeSameHours(timeline) {
  let temp = [];
  for (let i = 0; i < timeline.length; i++) {
    let currentChecker = timeline[i];
    for (let j = i + 1; j < timeline.length; j++) {
      if (
        parseInt(timeline[i]["hour"].split(":")[0]) ===
        parseInt(timeline[j]["hour"].split(":")[0])
      ) {
        currentChecker["inCount"] += timeline[j]["inCount"];
        currentChecker["outCount"] += timeline[j]["outCount"];
        currentChecker["total"] += timeline[j]["total"];
      }
    }
    temp.push(currentChecker);
  }

  return temp;
}

function fillLine(timeline) {
  let temp = [];

  // Minus 1 to hide current hour (if now it's 10:40, show in the chart only until 9:59)
  for (let i = 0; i <= new Date().getHours() - 1; i++) {
    let hour = i < 10 ? `0${i}:00` : `${i}:00`;
    let res = findHourData(timeline, hour);

    if (res !== -1) {
      temp.push(res);
    } else {
      temp.push({
        hour: hour,
        inCount: 0,
        outCount: 0,
      });
    }
  }
  return temp;
}
const CustomTooltips = ({ active, payload, label }) => {
  // console.log(`payload ${payload} `);

  if (active && payload && payload.length) {
    if (
      payload[0] &&
      payload[1] &&
      payload[0].value === 0 &&
      payload[1].value === 0
    ) {
      return null;
    } else if (payload[0] && payload[0].value === 0) {
      return null;
    } else if (payload[1] && payload[1].value === 0) {
      return null;
    }
    return (
      <div className="bg-white shadow-xl p-4 text-sm border rounded-md space-y-1 animate-fadeIn">
        {
          <p>{`${payload[0].payload["hour"]}-${
            payload[0].payload["hour"].split(":")[0]
          }:59 `}</p>
        }
        {payload[0] && (
          <p
            className={clsx({
              "text-blue-500 text-opacity-80": payload[0].dataKey === "inCount",
            })}
          >{`Entering: ${payload[0].value.toLocaleString()}`}</p>
        )}
        {payload[1] && (
          <p
            className={clsx({
              "text-indigo-500 text-opacity-80":
                payload[1].dataKey === "outCount",
            })}
          >{`Leaving: ${payload[1].value.toLocaleString()}`}</p>
        )}

        {/* <p className="text-gray-400">Total: {totalVisits.toLocaleString()}</p> */}
      </div>
    );
  }

  return null;
};

const ChartStatisticsInOut = () => {
  const [timelineData, setTimelineData] = useState([]);
  const { config } = useConfig();
  const { auth } = useAuth();

  const [devices, setDevices] = useState(new Map());

  const [finishSetup, setFinishSetup] = useState(false);

  const [loading, setLoading] = useState(true);

  function initDevices() {
    let devices = new Map();
    config.areas[3]["devices"].forEach((device, index) => {
      devices.set(device.deviceId, { azimuth: device.azimuth, index: index });
    });
    setDevices((prev) => devices);
  }

  function updateTimelineData() {
    if (request_updateTimelineData_safe === false) {
      return;
    } else {
      request_updateTimelineData_safe = false;
    }
    axios
      .get(
        `/v1/statistics/direction/hour?token=${
          auth.token
        }&day=${new Date().getUTCDate()}&month=${new Date().getUTCMonth() + 1}`
        // {
        //   withCredentials: true,
        // }
      )
      .then((res) => {
        let timeline = [];
        for (let i = 0; i < res.data.length; i++) {
          for (let j = 0; j < res.data[i].length; j++) {
            // Minus 1 to hide current hour (if now it's 10:40, show in the chart only until 9:59)
            if (
              new Date(res.data[i][j]["date"]).getHours() <=
              new Date().getHours()
            ) {
              if (devices.has(res.data[i][j]["sensor"])) {
                let toNorthEastCount = res.data[i][j]["dimXY"]
                  .map((val) => {
                    return val["directions"].filter((val) => {
                      return val === "toNorthEast";
                    }).length;
                  })
                  .reduce((a, b) => a + b);

                let toNorthWestCount = res.data[i][j]["dimXY"]
                  .map((val) => {
                    return val["directions"].filter((val) => {
                      return val === "toNorthWest";
                    }).length;
                  })
                  .reduce((a, b) => a + b);
                let toSouthEastCount = res.data[i][j]["dimXY"]
                  .map((val) => {
                    return val["directions"].filter((val) => {
                      return val === "toSouthEast";
                    }).length;
                  })
                  .reduce((a, b) => a + b);
                let toSouthWestCount = res.data[i][j]["dimXY"]
                  .map((val) => {
                    return val["directions"].filter((val) => {
                      return val === "toSouthWest";
                    }).length;
                  })
                  .reduce((a, b) => a + b);
                let inCount = 0;
                let outCount = 0;
                let device = devices.get(res.data[i][j]["sensor"]);
                if (device.azimuth < 180) {
                  if (device.azimuth > 135) {
                    if (device.index === 1) {
                      //device 2
                      inCount = toNorthEastCount + toNorthWestCount;
                      outCount = toSouthEastCount + toSouthWestCount;
                    } else if (device.index === 4) {
                      //device 5
                      inCount = toSouthEastCount + toNorthEastCount;
                      outCount = toSouthWestCount + toNorthWestCount;
                    }
                  } else {
                    //device 3
                    inCount = toSouthEastCount + toSouthWestCount;
                    outCount = toNorthEastCount + toNorthWestCount;
                  }
                } else {
                  if (device.azimuth > 315) {
                    //device 1
                    inCount = toNorthEastCount + toNorthWestCount;
                    outCount = toSouthEastCount + toSouthWestCount;
                  } else {
                    if (device.index === 3) {
                      //device 4
                      inCount = toNorthEastCount + toSouthEastCount;
                      outCount = toNorthWestCount + toSouthWestCount;
                    } else if (device.index === 5) {
                      //device 6
                      inCount = toNorthWestCount + toSouthWestCount;
                      outCount = toNorthEastCount + toSouthEastCount;
                    }
                  }
                }

                let total = inCount > outCount ? inCount : outCount;

                timeline.push({
                  hour: getLocalHour(res.data[i][j]["date"]),
                  inCount: inCount !== undefined ? inCount : 0,
                  outCount: outCount !== undefined ? outCount : 0,
                  total: total !== undefined ? total : 0,
                });
              }
            }
          }
        }
        // TODO: need to check if this if needed
        // if (JSON.stringify(timelineData) !== JSON.stringify(resData)) {
        timeline = margeSameHours(timeline);
        timeline = fillLine(timeline);

        setTimelineData((prev) => timeline);
        setFinishSetup(true);

        setLoading(false);
        // setLoading(false);
        // if (totalCountVehicles !== totalVehicles) {
        //   setTotalCountVehicles((prev) => totalVehicles);
        // }
        request_updateTimelineData_safe = true;
      })
      .catch((err) => {
        console.log(
          "error with get hour statistic inside today vehicles-> ",
          err
        );
      });
  }

  useEffect(() => {
    initDevices();
    updateTimelineData();
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the up-to-date data for the in-out chart every 1 sec
  useInterval(
    () => {
      updateTimelineData();
    },
    !finishSetup ? 1000 : 10000,
    true
  );

  if (loading) {
    return (
      <div className="bg-gray-300 flex-auto w-full h-full rounded-md overflow-hidden transition-all">
        <div className="w-full h-full opacity-50 transform -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent  animate-slide"></div>
      </div>
    );
  }

  return (
    <div className="w-full h-full  bg-white rounded-lg p-2">
      <div className="flex flex-row justify-between">
        <div className="uppercase text-xs font-semibold text-gray-400">
          Today
        </div>
        <div className="flex flex-row space-x-2 items-center select-none"></div>
      </div>
      <div>
        <div className="font-bold text-sm text-gray-800">
          Park Dubnov Hourly People
        </div>
        <p className="text-sm  opacity-80 px-1">{new Date().toDateString()}</p>
      </div>

      <div className="w-full h-4/6 bg-gray-100 bg-opacity-50">
        <div className="flex flex-row">
          <div className="flex items-center flex-wrap border-2 rounded-md bg-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 "
              viewBox="0 0 20 20"
              fill="#013987"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-xs  text-blue-800   px-1">Entering The Park</p>
          </div>
          <div className="flex items-center flex-wrap border-2 rounded-md bg-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 "
              viewBox="0 0 20 20"
              fill="#7206c4"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-xs text-indigo-800  px-1">Leaving The Park</p>
          </div>
        </div>

        <ResponsiveContainer>
          <AreaChart
            data={timelineData}
            margin={{ top: 5, right: 12, left: 6, bottom: 20 }}
          >
            <defs>
              {/* <linearGradient id="leaving" x1="0" y1="0" x2="0" y2="1">
              <stop offset="100%" stopColor="rgb(156, 163, 175)" />
            </linearGradient> */}
              <linearGradient id="inCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="50%" stopColor="#0054c9" opacity={"0.8"} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="outCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="50%" stopColor="#7206c4" opacity={0.5} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip
              coordinate={{ x: 0, y: 0 }}
              offset={0}
              allowEscapeViewBox={{ x: false, y: true }}
              content={<CustomTooltips />}
            />
            <XAxis
              axisLine={false}
              tick={{ fontSize: "0.5rem" }}
              interval="preserveStartEnd"
              // interval={0}
              // angle={-10}
              // dx={20}
              // dy={10}
              height={15}
              dataKey="hour"
            />
            <YAxis
              axisLine={false}
              tickLine={true}
              // mirror={true}
              tick={{ fontSize: "0.6rem" }}
              // allowDataOverflow={false}
              // tickMargin={5}
              width={30}
              // dx={10}
              // hide={true}
              interval="preserveStartEnd"
              dataKey="total"
            />
            <CartesianGrid strokeDasharray="3 3" />
            <Area
              type="natural"
              dataKey="inCount"
              stroke="#013987"
              strokeWidth={1}
              fillOpacity={0.6}
              fill={"url(#inCount)"}
            />
            <Area
              type="natural"
              dataKey="outCount"
              stroke="#4d0187"
              strokeWidth={1}
              fillOpacity={0.6}
              fill={"url(#outCount)"}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  //OTHER LOGIC TGO SAVE FOR THE FUTURE
  // return (
  //   <div className="p-4">
  //     <div className=" font-semibold text-lg text-gray-600 px-2.5 pt-2">
  //       West To East Count
  //     </div>
  //     <div className="w-full 2xl:h-56 xl:h-60 bg-gray-100 bg-opacity-50">
  //       <ResponsiveContainer>
  //         <AreaChart
  //           data={toEast !== undefined ? toEast : toWest}
  //           margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
  //         >
  //           <defs>
  //             {/* <linearGradient id="leaving" x1="0" y1="0" x2="0" y2="1">
  //             <stop offset="100%" stopColor="rgb(156, 163, 175)" />
  //           </linearGradient> */}
  //             <linearGradient
  //               id={toEast !== undefined ? "northToEast" : "eastToNorth"}
  //               x1="0"
  //               y1="0"
  //               x2="0"
  //               y2="1"
  //             >
  //               <stop
  //                 offset="100%"
  //                 stopColor="rgb(59, 130, 246)"
  //                 opacity={0.6}
  //               />
  //             </linearGradient>
  //             <linearGradient
  //               id={toEast !== undefined ? "westToEast" : "eastToWest"}
  //               x1="0"
  //               y1="0"
  //               x2="0"
  //               y2="1"
  //             >
  //               <stop
  //                 offset="100%"
  //                 stopColor="rgb(59, 246, 87)"
  //                 opacity={0.6}
  //               />
  //             </linearGradient>
  //           </defs>
  //           <Tooltip
  //             coordinate={{ x: 0, y: 0 }}
  //             offset={0}
  //             allowEscapeViewBox={{ x: false, y: true }}
  //             content={<CustomTooltip />}
  //           />
  //           <XAxis
  //             axisLine={false}
  //             tick={{ fontSize: "0.5rem" }}
  //             interval="preserveStartEnd"
  //             // interval={0}
  //             // angle={-10}
  //             // dx={20}
  //             // dy={10}
  //             height={15}
  //             dataKey="hour"
  //           />
  //           <YAxis
  //             axisLine={false}
  //             tickLine={true}
  //             // mirror={true}
  //             tick={{ fontSize: "0.6rem" }}
  //             // allowDataOverflow={false}
  //             // tickMargin={5}
  //             width={30}
  //             // dx={10}
  //             // hide={true}
  //             interval="preserveStartEnd"
  //             dataKey="total"
  //           />

  //           <Area
  //             type="natural"
  //             dataKey={toEast !== undefined ? "northToEast" : "eastToNorth"}
  //             stroke="rgb(59, 130, 246)"
  //             strokeWidth={2.5}
  //             fillOpacity={0.2}
  //             fill={
  //               toEast !== undefined ? "url(#northToEast)" : "url(#eastToNorth)"
  //             }
  //           />
  //           <Area
  //             type="natural"
  //             dataKey={toEast !== undefined ? "westToEast" : "eastToWest"}
  //             stroke="rgb(59, 246, 87)"
  //             strokeWidth={2.5}
  //             fillOpacity={0.2}
  //             fill={
  //               toEast !== undefined ? "url(#westToEast)" : "url(#eastToWest)"
  //             }
  //           />
  //         </AreaChart>
  //       </ResponsiveContainer>
  //     </div>
  //   </div>
  // );

  // <div className="p-4">
  //   <div className=" font-semibold text-lg text-gray-600 px-2.5 pt-2">
  //     West To East Count
  //   </div>
  //   <div className="w-full 2xl:h-56 xl:h-60 bg-gray-100 bg-opacity-50">
  //     <ResponsiveContainer>
  //       <AreaChart
  //         data={toEast !== undefined ? toEast : toWest}
  //         margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
  //       >
  //         <defs>
  //           {/* <linearGradient id="leaving" x1="0" y1="0" x2="0" y2="1">
  //             <stop offset="100%" stopColor="rgb(156, 163, 175)" />
  //           </linearGradient> */}
  //           <linearGradient
  //             id={toEast !== undefined ? "northToEast" : "eastToNorth"}
  //             x1="0"
  //             y1="0"
  //             x2="0"
  //             y2="1"
  //           >
  //             <stop
  //               offset="100%"
  //               stopColor="rgb(59, 130, 246)"
  //               opacity={0.6}
  //             />
  //           </linearGradient>
  //           <linearGradient
  //             id={toEast !== undefined ? "westToEast" : "eastToWest"}
  //             x1="0"
  //             y1="0"
  //             x2="0"
  //             y2="1"
  //           >
  //             <stop
  //               offset="100%"
  //               stopColor="rgb(59, 246, 87)"
  //               opacity={0.6}
  //             />
  //           </linearGradient>
  //         </defs>
  //         <Tooltip
  //           coordinate={{ x: 0, y: 0 }}
  //           offset={0}
  //           allowEscapeViewBox={{ x: false, y: true }}
  //           content={<CustomTooltip />}
  //         />
  //         <XAxis
  //           axisLine={false}
  //           tick={{ fontSize: "0.5rem" }}
  //           interval="preserveStartEnd"
  //           // interval={0}
  //           // angle={-10}
  //           // dx={20}
  //           // dy={10}
  //           height={15}
  //           dataKey="hour"
  //         />
  //         <YAxis
  //           axisLine={false}
  //           tickLine={true}
  //           // mirror={true}
  //           tick={{ fontSize: "0.6rem" }}
  //           // allowDataOverflow={false}
  //           // tickMargin={5}
  //           width={30}
  //           // dx={10}
  //           // hide={true}
  //           interval="preserveStartEnd"
  //           dataKey="total"
  //         />

  //         <Area
  //           type="natural"
  //           dataKey={toEast !== undefined ? "northToEast" : "eastToNorth"}
  //           stroke="rgb(59, 130, 246)"
  //           strokeWidth={2.5}
  //           fillOpacity={0.2}
  //           fill={
  //             toEast !== undefined ? "url(#northToEast)" : "url(#eastToNorth)"
  //           }
  //         />
  //         <Area
  //           type="natural"
  //           dataKey={toEast !== undefined ? "westToEast" : "eastToWest"}
  //           stroke="rgb(59, 246, 87)"
  //           strokeWidth={2.5}
  //           fillOpacity={0.2}
  //           fill={
  //             toEast !== undefined ? "url(#westToEast)" : "url(#eastToWest)"
  //           }
  //         />
  //       </AreaChart>
  //     </ResponsiveContainer>
  //   </div>
  // </div>
  //  :
  // <div className="p-4">
  // <div className=" font-semibold text-lg text-gray-600 px-2.5 pt-2">
  //   West To East Count
  // </div>
  // <div className="w-full 2xl:h-56 xl:h-60 bg-gray-100 bg-opacity-50">
  //   <ResponsiveContainer>
  //     <AreaChart
  //       data={toWest}
  //       margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
  //     >
  //       <defs>
  //         {/* <linearGradient id="leaving" x1="0" y1="0" x2="0" y2="1">
  //           <stop offset="100%" stopColor="rgb(156, 163, 175)" />
  //         </linearGradient> */}
  //         <linearGradient id="northToEast" x1="0" y1="0" x2="0" y2="1">
  //           <stop
  //             offset="100%"
  //             stopColor="rgb(59, 130, 246)"
  //             opacity={0.8}
  //           />
  //         </linearGradient>
  //         <linearGradient id="westToEast" x1="0" y1="0" x2="0" y2="1">
  //           <stop
  //             offset="100%"
  //             stopColor="rgb(59, 246, 87)"
  //             opacity={0.6}
  //           />
  //         </linearGradient>
  //       </defs>
  //       <Tooltip
  //         key={id}
  //         coordinate={{ x: 0, y: 0 }}
  //         offset={0}
  //         allowEscapeViewBox={{ x: false, y: true }}
  //         content={<CustomTooltip />}
  //       />
  //       <XAxis
  //         axisLine={false}
  //         tick={{ fontSize: "0.5rem" }}
  //         interval="preserveStartEnd"
  //         // interval={0}
  //         // angle={-10}
  //         // dx={20}
  //         // dy={10}
  //         height={15}
  //         dataKey="hour"
  //       />
  //       <YAxis
  //         axisLine={false}
  //         tickLine={true}
  //         // mirror={true}
  //         tick={{ fontSize: "0.6rem" }}
  //         // allowDataOverflow={false}
  //         // tickMargin={5}
  //         width={30}
  //         // dx={10}
  //         // hide={true}
  //         interval="preserveStartEnd"
  //         dataKey="total"
  //       />
  //       <Area
  //         type="natural"
  //         dataKey="northToEast"
  //         stroke="rgb(59, 130, 246)"
  //         strokeWidth={2.5}
  //         fillOpacity={0.2}
  //         fill="url(#northToEast)"
  //       />
  //       <Area
  //         type="natural"
  //         dataKey="westToEast"
  //         stroke="rgb(59, 246, 87)"
  //         strokeWidth={2.5}
  //         fillOpacity={0.2}
  //         fill="url(#westToEast)"
  //       />
  //     </AreaChart>
  //   </ResponsiveContainer>
  // </div>

  // return (
  //   <div className="p-4">
  //     <div className="w-full h-32 bg-gray-100 bg-opacity-50">
  //       <ResponsiveContainer>
  //         <AreaChart
  //           data={timelineData}
  //           margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
  //         >
  //           <defs>
  //             {/* <linearGradient id="leaving" x1="0" y1="0" x2="0" y2="1">
  //               <stop offset="100%" stopColor="rgb(156, 163, 175)" />
  //             </linearGradient> */}
  //             <linearGradient id="vehicles" x1="0" y1="0" x2="0" y2="1">
  //               <stop offset="100%" stopColor="rgb(59, 130, 246)" />
  //             </linearGradient>
  //           </defs>
  //           <Tooltip
  //             coordinate={{ x: 0, y: 0 }}
  //             offset={0}
  //             allowEscapeViewBox={{ x: false, y: true }}
  //             content={<CustomTooltip />}
  //           />
  //           <XAxis
  //             axisLine={false}
  //             tick={{ fontSize: "0.5rem" }}
  //             interval="preserveStartEnd"
  //             // interval={0}
  //             // angle={-10}
  //             // dx={20}
  //             // dy={10}
  //             height={15}
  //             dataKey="hour"
  //           />
  //           <YAxis
  //             axisLine={false}
  //             tickLine={true}
  //             // mirror={true}
  //             tick={{ fontSize: "0.6rem" }}
  //             // allowDataOverflow={false}
  //             // tickMargin={5}
  //             width={30}
  //             // dx={10}
  //             // hide={true}
  //             interval="preserveStartEnd"
  //             dataKey="vehicles"
  //           />
  //           {
  //             <Area
  //               type="natural"
  //               dataKey="vehicles"
  //               stroke="rgb(59, 130, 246)"
  //               strokeWidth={2.5}
  //               fillOpacity={0.2}
  //               fill="url(#vehicles)"
  //             />
  //           }
  //         </AreaChart>
  //       </ResponsiveContainer>
  //     </div>
  //   </div>
  // );
  // };
};

export default ChartStatisticsInOut;
